import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import { commonInputStyles, ColumnName, SavingSpinnerForColumnEditors, ActionButtonsForColumnEditors, handleClose, ErrorMessageViewer } from "./ColumnEditorFactory";
import { validateNumberInputsBeforeSaving } from "../RowId/Validations";

const NumberReusableEditor = ({
  initialValue = "",
  open,
  data,
  column,
  columnName,
  onSave,
  onCancel,
}) => {
  const safeInitialValue = initialValue === null || initialValue === undefined ? "" : String(initialValue);
  const inputRef = useRef(null);
  const [value, setValue] = useState(safeInitialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });

  const columnTypeId = String(column?.column_type_id ?? "5");
  const decimals = Number(column?.normalizedColumnOptions?.decimals) ?? 2;

  useEffect(() => {
    if (open) {
      const formattedValue = getFormattedDefaultValue(safeInitialValue, columnTypeId, decimals);
      setValue(formattedValue);
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        setValidationError({ isInvalid: false, message: "" });
      }
    }
  }, [initialValue, open, columnTypeId, decimals]);

  const getFormattedDefaultValue = (raw, type, decimals) => {
    if (raw === "") return "";
    const isCharPress = isNaN(raw) && raw !== "";
    if (type === "22") {
      return isCharPress ? raw : ((Number(raw) * 100).toFixed(decimals - 2));
    }
    if (type === "5" || type === "17") {
      return isCharPress ? raw : Number(raw).toFixed(decimals);
    }
    return raw ?? "";
  };

  const parseFinalValue = (val) => {
    if(val === null || val === undefined) return "";
    const raw = val.toString().replace(/[,]/g, "").replace("%", "");
    if (raw.trim() === "") return "";
    const num = parseFloat(raw || "0");
    if (isNaN(num)) return "";

    if (columnTypeId === "22") {
      return (num / 100).toFixed(decimals ?? 2);
    }
    if (columnTypeId === "5" || columnTypeId === "17") {
      return num.toFixed(decimals);
    }
    return raw;
  };

  const handleChange = (e) => {
    setValue(e.target.value);
    if (validationError.isInvalid) {
      setValidationError({ isInvalid: false, message: "" });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsedValue = parseFinalValue(value);
      if (Number(parsedValue) === Number(initialValue)) {
        onCancel();
        return;
      }

      const isUpdateAllowed = validateNumberInputsBeforeSaving(parsedValue, column, data);
      if (isUpdateAllowed.isInvalid) {
        setValidationError(isUpdateAllowed);
        return;
      }

      await onSave(parsedValue, initialValue ?? 0, column);
    } catch (err) {
      console.error("Error saving value:", err);
      // setIsSaving(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(Number(parseFinalValue(value)), Number(initialValue), onCancel, validationError, setValidationError)}
      fullWidth
      PaperProps={{
        style: {
          width: "420px",
          height: validationError.isInvalid ? "190px" : "160px",
          borderRadius: "8px",
        },
      }}
    >
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 8 }}>
          {columnName && <ColumnName columnName={columnName}/>}
          <input
            ref={inputRef}
            type="number"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            step={1 / Math.pow(10, decimals)}
            // step={1}
            disabled={isSaving}
            style={{
              ...commonInputStyles,
              height: "40px",
              boxSizing: "border-box",
            }}
          />

          {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

          <ActionButtonsForColumnEditors handleSave={handleSave} handleCancel={() => handleClose(Number(parseFinalValue(value)), Number(initialValue), onCancel, validationError, setValidationError)} isSaveDisabled={isSaving} isCloseDisabled={isSaving} />

          {isSaving && (<SavingSpinnerForColumnEditors />)}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NumberReusableEditor;
